/**
 * Occupational Therapy Evaluation Note — options + empty payload.
 * After shared common header (1–6): Living Situation → Subjective → Objective →
 * Clinical Assessment → Plan of Care (per product screenshots).
 */

import { emptyOtCommonHeader } from "./otNotesCommonHeaderUtils";
import { emptyTextGoals } from "../ptNotes/ptGoals";

/** Pain pills matching OT eval mock (0 / 2 / 5 / 7 / 10). */
export const OT_PAIN_SCALE = [
  { value: 0, label: "None", tone: "emerald" },
  { value: 2, label: "Mild", tone: "lime" },
  { value: 5, label: "Mod", tone: "amber" },
  { value: 7, label: "Severe", tone: "orange" },
  { value: 10, label: "Worst", tone: "rose" },
];

export const LIVES_WITH_OPTIONS = [
  "Alone",
  "Spouse",
  "Family",
  "Caregiver",
  "Other",
];

export const RESIDENCE_TYPE_OPTIONS = [
  "Private Home / Single Level",
  "Private Home / Multi Level",
  "Apartment",
  "Assisted Living",
  "SNF",
  "Other",
];

export const ENTRANCE_ACCESS_OPTIONS = ["Level", "Steps", "Ramp"];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const WFL_IMPAIRED_OPTIONS = ["WFL", "Impaired"];

export const WFL_DEFICIT_OPTIONS = ["WFL", "Deficit"];

export const GOOD_FAIR_POOR_OPTIONS = ["Good", "Fair", "Poor"];

export const DOMINANT_HAND_OPTIONS = ["R", "L"];

export const MENTAL_COGNITIVE_OPTIONS = [
  "Alert & Oriented x4",
  "Follows 3-step cmd",
  "Short-term Deficit",
  "Safety Impairment",
];

export const UE_JOINT_MOTIONS = [
  "Shoulder Flexion",
  "Shoulder Abduction",
  "Elbow Flexion",
  "Elbow Extension",
  "Wrist Flexion",
  "Wrist Extension",
  "Wrist / Grip",
  "Hand Grip",
];

export const emptyUeFunctionRow = (joint) => ({
  joint,
  aromRight: "",
  aromLeft: "",
  mmtRight: "",
  mmtLeft: "",
});

export const emptyUeFunctionRows = () =>
  UE_JOINT_MOTIONS.map((joint) => emptyUeFunctionRow(joint));

/** ADL/IADL assistance levels (columns). */
export const ADL_ASSIST_LEVELS = [
  { key: "IND", label: "IND" },
  { key: "MOD_I", label: "MOD-I" },
  { key: "MIN_A", label: "MIN-A" },
  { key: "MOD_A", label: "MOD-A" },
  { key: "MAX_A", label: "MAX-A" },
  { key: "DEP", label: "DEP" },
];

export const ADL_TASKS = [
  "Upper Body Dressing",
  "Bathing / Showering",
  "Feeding / Eating",
  "Meal Preparation",
  "Light Housekeeping",
  "Medication Management",
  "Transportation",
  "Housekeeping",
  "Toileting",
  "Toilet Transfer",
  "Tub/Shower Transfer",
  "Functional Mobility",
  "Bed Mobility",
];

export const emptyAdlRows = () =>
  ADL_TASKS.map((task) => ({
    task,
    level: "",
  }));

export const IDENTIFIED_DEFICIT_OPTIONS = [
  "Decreased ADL Performance",
  "Decreased UE Strength / ROM",
  "Decreased Activity Tolerance",
  "Safety Issues with IADLs",
  "Cognitive Impairment",
  "Decreased IADL Performance",
  "Others",
];

export const REHAB_POTENTIAL_OPTIONS = ["Good", "Fair", "Poor"];

export const OT_INTERVENTION_OPTIONS = [
  "ADL Training",
  "Energy Conservation",
  "IADL Training",
  "Home Safety Training",
  "Therapeutic Exercise",
  "Cognitive Retraining",
  "Therapeutic Activities",
  "Patient / Caregiver Education",
  "Functional Mobility Training",
  "Home Exercise Program",
  "Transfer Training",
  "Other",
];

export const SPLINT_EQUIPMENT_OPTIONS = [
  "None",
  "Splint / Orthotic",
  "Adaptive Equipment",
];

export const ENDURANCE_FLAG_OPTIONS = [
  "SOB (Shortness of Breath) with activity",
  "Fatigues Easily",
];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

/** Mutual-exclusive None vs other splint/equipment selections. */
export const toggleSplintEquipment = (list, item) => {
  const current = Array.isArray(list) ? list : [];
  if (item === "None") {
    return current.includes("None") ? [] : ["None"];
  }
  const withoutNone = current.filter((x) => x !== "None");
  return withoutNone.includes(item)
    ? withoutNone.filter((x) => x !== item)
    : [...withoutNone, item];
};

export const coerceExclusiveChoice = (value) => {
  if (Array.isArray(value)) return value.find(Boolean) || "";
  return value || "";
};

/** Full editable OT Evaluation payload (JSON on dedicated row). */
export const emptyOtEvalPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  commonHeader: emptyOtCommonHeader(),

  // —— 7. Living Situation ——
  residenceType: "",
  livesWith: "",
  entranceAccess: "",
  railingPresent: "",

  // —— 8. Subjective History ——
  chiefComplaintGoals: "",
  historyPresentIllness: "",
  plof: "",
  currentLevelOfFunction: "",
  medicationHistory: "",
  allergiesNarrative: "",
  currentMedicationsNarrative: "",
  relievingFactors: "",
  aggravatingFactors: "",
  painLevel: null,

  // —— 9. Objective: Mental & Cognitive ——
  mentalCognitive: [],
  cognitiveComments: "",

  // —— 9. Objective: Vision & Perception ——
  visionStatus: "",
  wearsGlasses: "",
  depthPerception: "",

  // —— 9. Objective: UE Function ——
  ueFunction: emptyUeFunctionRows(),
  ueFunctionComments: "",

  // —— 9. Objective: ADL/IADL ——
  adlPerformance: emptyAdlRows(),
  adlComments: "",

  // —— 9. Objective: Hand Function ——
  dominantHand: "",
  gripLbsRight: "",
  gripLbsLeft: "",
  pinchLateralRight: "",
  pinchLateralLeft: "",
  pinchThreePointRight: "",
  pinchThreePointLeft: "",
  handFunctionComments: "",

  // —— 9. Objective: Balance ——
  sittingBalance: "",
  standingBalance: "",
  balanceComments: "",

  // —— 9. Objective: Endurance ——
  enduranceTolerance: "",
  enduranceFlags: [],
  enduranceComments: "",

  // —— 9. Objective: Splints & Equipment ——
  splintsEquipment: [],
  splintsEquipmentOther: "",
  splintsEquipmentComments: "",

  // —— 10. Clinical Assessment ——
  clinicalImpression: "",
  identifiedDeficits: [],
  identifiedDeficitsOther: "",
  rehabPotential: "",

  // —— 11. Plan of Care ——
  visitFrequency: "",
  anticipatedDischargeDate: "",
  interventions: [],
  interventionOther: "",
  shortTermGoals: emptyTextGoals(1, "st"),
  longTermGoals: emptyTextGoals(1, "lt"),
});

export const OT_EVAL_ARRAY_KEYS = [
  "mentalCognitive",
  "enduranceFlags",
  "splintsEquipment",
  "identifiedDeficits",
  "interventions",
];
